//src/types/Account/Pilot.ts

import type { AircraftType } from '../Helper/Enums'
import { Part141Courses } from '../Part141/Part141Enums'
import type { OtherCheckItem } from '@/utils/pilotOtherChecks'
import {
  AircraftClass,
  InstructorRatings,
  MedicalType,
  PilotRatings,
  type GroundInstructorRatings
} from './AccountEnums'

export interface Pilot {
  numFlights: number
  newStudent: boolean

  loa: boolean
  loaInfo: string

  annualRecurrency: boolean
  flightReview: Date | null

  ratings: (RatingForACType | null)[]

  times: PilotTimes

  medical: MedicalType
  medicalDOI: Date | null
  medicalExp: Date | null
  medicalExpOverride: boolean

  groundTow: boolean
  groundTowDate: Date | null
  cat2: boolean
  cat2Date: Date | null
  cat3: boolean
  cat3Date: Date | null

  groundInsRatings: GroundInstructorRatings[]
  groundInsCert: Certificate | null

  checks: AircraftType[]
  otherChecks: OtherCheckItem[]

  students: string[]
  instructor: string

  previousSchool: string
  psGrndHours: number
  psFlightHours: number

  currencies: PilotCurrency[]
  makeModelHours: MakeModelHours[]

  insurance: RentersInsurance

  enrolledCourses: Part141Courses[] //Enrolled Training Courses
  lastFlight: Date | null

  enrolled141: boolean // actively enrolled in ≥1 141 course (not yet graduated/terminated/transferred)
  enrolled61: boolean // actively enrolled in ≥1 61 course (does NOT drive the dispatch gate)
}

export interface PilotTimes {
  totalTime: number
  /** Ground instruction — from ground sessions, not flights. */
  groundTime: number
  pICTime: number
  dayTime: number
  dualTime: number
  crossCountry: number
  ftdAtd: number
  ffs: number
  simulator: number
  landingsDay: number
  landingsNight: number
  landingsPIC: number
  actualInstTime: number
  simulatedInstTime: number
  instApproaches: number
  dualnightTime: number
  nightTime: number
  dualCrossCountry: number
  dualCrossCountryNight: number
  dualCrossCountryDay: number
  soloTime: number
  soloNight: number
  soloCrossCountryDay: number
  soloCrossCountryNight: number
  pICCrossCountry: number
  pressurization: number
  highAltitude: number
  highPerformance: number
  complex: number
  multi: number
  taa: number
  tailwheel: number
  landHours: number
  seaHours: number
  prePost: number
}

// // Handy helpers that always stay in sync:
// export type PilotTimeKey = (typeof PILOT_TIME_KEYS)[number]
// export const emptyPilotTimes = (): PilotTimes => createPilotTimes()

// 1) Constant template = single source of truth
export const PILOT_TIME_TEMPLATE: Readonly<PilotTimes> = {
  totalTime: 0,
  groundTime: 0,
  pICTime: 0,
  dayTime: 0,
  dualTime: 0,
  crossCountry: 0,
  ftdAtd: 0,
  ffs: 0,
  simulator: 0,
  landingsDay: 0,
  landingsNight: 0,
  landingsPIC: 0,
  complex: 0,
  multi: 0,
  taa: 0,
  actualInstTime: 0,
  simulatedInstTime: 0,
  instApproaches: 0,
  dualnightTime: 0,
  nightTime: 0,
  dualCrossCountry: 0,
  dualCrossCountryNight: 0,
  dualCrossCountryDay: 0,
  soloTime: 0,
  soloNight: 0,
  soloCrossCountryDay: 0,
  soloCrossCountryNight: 0,
  pICCrossCountry: 0,
  pressurization: 0,
  highAltitude: 0,
  highPerformance: 0,
  tailwheel: 0,
  landHours: 0,
  seaHours: 0,
  prePost: 0
}

// 2) Factory returns a fresh object each time
export function createPilotTimes(): PilotTimes {
  return { ...PILOT_TIME_TEMPLATE }
}

// 3) Keys and types derived from the template
export const PILOT_TIME_KEYS = Object.keys(
  PILOT_TIME_TEMPLATE
) as (keyof typeof PILOT_TIME_TEMPLATE)[]
export type PilotTimeKey = (typeof PILOT_TIME_KEYS)[number]
export const emptyPilotTimes = (): PilotTimes => ({ ...PILOT_TIME_TEMPLATE })

/**
 * Display labels for every logged-time field — the one table used by the
 * Course Times card, the prior-time entry block and the minimums editor.
 */
export const PILOT_TIME_LABELS: Readonly<Record<keyof PilotTimes, string>> = {
  totalTime: 'Total Time',
  groundTime: 'Ground Instruction',
  pICTime: 'PIC',
  dayTime: 'Day (Total)',
  dualTime: 'Dual',
  crossCountry: 'Cross Country',
  ftdAtd: 'FTD/ATD',
  ffs: 'FFS',
  simulator: 'Simulator',
  landingsDay: 'Landings Day',
  landingsNight: 'Landings Night',
  landingsPIC: 'Landings PIC',
  complex: 'Complex',
  multi: 'Multi Engine',
  taa: 'TAA',
  actualInstTime: 'Inst Actual',
  simulatedInstTime: 'Inst Simulated',
  instApproaches: 'Inst Approaches',
  dualnightTime: 'Dual Night',
  nightTime: 'Night (Total)',
  dualCrossCountry: 'Dual XC',
  dualCrossCountryNight: 'Dual XC Night',
  dualCrossCountryDay: 'Dual XC Day',
  soloTime: 'Solo',
  soloNight: 'Solo Night',
  soloCrossCountryDay: 'Solo XC Day',
  soloCrossCountryNight: 'Solo XC Night',
  pICCrossCountry: 'PIC XC',
  pressurization: 'Pressurization',
  highAltitude: 'High Altitude',
  highPerformance: 'High Performance',
  tailwheel: 'Tailwheel',
  landHours: 'Land',
  seaHours: 'Sea',
  prePost: 'Pre/Post Flight'
}

// 4) ✅ Copier util (NO f/x here—parameters instead)
export function assignPilotTimes<T extends object>(target: T, src: Partial<PilotTimes>) {
  for (const k of PILOT_TIME_KEYS) {
    const n = Number(src[k] as unknown)
    if (Number.isFinite(n)) (target as any)[k] = n
  }
}

export interface Certificate {
  doi: Date | null
  number: string
}

export interface PilotCurrency {
  lastCurrentAircraft: string //tailNumber
  lastFlownDate: Date
  hasSignOff?: boolean
  hasMgmntSignOff?: boolean
  signedOffBy?: string
}

export enum CurrencyIndexes {
  Category = 0,
  Class = 1,
  Exact = 2,
  MakeModel = 3
}

export interface MakeModelHours {
  makeModel: string
  hours: number
}

export interface RatingForACType {
  pilotRatings: PilotRatings[]
  pilotCert: Certificate | null

  instructorRatings: InstructorRatings[]
  instructorCert: Certificate | null
}

export interface RentersInsurance {
  insCo: string
  insPolcyID: string
  insDate: Date | null
}

export function createPilot(init: Partial<Pilot> = {}): Pilot {
  const pilot = {
    numFlights: 0,
    newStudent: false,

    loa: false,
    loaInfo: '',

    ratings: Array(Object.values(AircraftClass).length).fill(null),

    times: createPilotTimes(),

    medical: MedicalType.none,
    medicalDOI: null,
    medicalExp: null,
    medicalExpOverride: false,

    groundTow: false,
    groundTowDate: null,
    cat2: false,
    cat2Date: null,
    cat3: false,
    cat3Date: null,

    groundInsRatings: [],
    groundInsCert: null,

    checks: [],
    otherChecks: [],

    annualRecurrency: false,
    flightReview: null,

    students: [],
    instructor: '',

    previousSchool: '',
    psGrndHours: 0,
    psFlightHours: 0,

    currencies: [],
    makeModelHours: [],

    insurance: {
      insCo: '',
      insPolcyID: '',
      insDate: null
    },

    enrolledCourses: [],
    lastFlight: null,

    enrolled141: false,
    enrolled61: false,

    ...init
  }

  if (!pilot.ratings || pilot.ratings.length === 0) {
    pilot.ratings = Array(Object.values(AircraftClass).length).fill(null)
  }

  return pilot
}
export function createRatingForACType(
  rating: PilotRatings,
  defaultCert: Certificate | undefined = undefined
): RatingForACType {
  return {
    pilotRatings: [rating],
    pilotCert: createCertificate(defaultCert),

    instructorRatings: [],
    instructorCert: null
  }
}

export function createCertificate(init: Partial<Certificate> = {}): Certificate {
  return {
    doi: null,
    number: '',
    ...init
  }
}
